import {
  STATE_MESSAGES,
  generateBrowserNonce,
  type AuthState,
  type BootstrapPayload,
  type ColorShieldConfig,
  type DemoScenario,
  type RuntimeTrustState,
  type StateDetail,
  type SubmitAuthorization,
  type SubmitAuthorizationDecision,
  type SubmitGateState,
  type TimelineEvent,
  type TimelineEventType,
  type VerifyResponse,
  type VisualTrustState,
} from "./types";
import {
  buildVisualTrustDetail,
  deriveVisualTrustState,
  renderVisualTrustSignal,
  resolveTrustMountHost,
  visualTrustDomContainsSecrets,
  type VisualTrustDetail,
} from "./visual-trust";

/** Default pre-submit timeout — keep aligned with protocol PRE_SUBMIT_TIMEOUT_MS. */
const DEFAULT_PRE_SUBMIT_TIMEOUT_MS = 5000;

export class ColorShieldClient {
  private config: ColorShieldConfig;
  private state: AuthState = "INITIALIZING";
  private runtimeTrustState: RuntimeTrustState = "INITIALIZING";
  private runtimeProof: string | null = null;
  private runtimeProofExpiresAtMs = 0;
  private lastRuntimeVerifiedAtMs = 0;
  private browserNonce: string | null = null;
  private bootstrap: BootstrapPayload | null = null;
  private token: string | null = null;
  private tokenExpiresAtMs = 0;
  private verifiedDomain: string | null = null;
  private verifiedPath: string | null = null;
  private verifiedFormId: string | null = null;
  private verifiedSessionId: string | null = null;
  private lastReason = "";
  private lastVerdict = "";
  private destroyed = false;
  private formEl: HTMLFormElement | null = null;
  private indicatorEl: HTMLElement | null = null;
  private trustSignalEl: HTMLElement | null = null;
  private trustAutoMounted = false;
  private readonly instanceId = `csid_${Math.random().toString(16).slice(2, 10)}`;
  private lastVisualState: VisualTrustState | null = null;
  private submitHandler: ((ev: Event) => void) | null = null;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private submitGateState: SubmitGateState = "IDLE";
  /** Ignore nested/recursive submit while releasing after PERMIT (native path). */
  private allowNativeSubmitOnce = false;
  /** Prevent double-click concurrent validation. */
  private submitInFlight = false;
  /**
   * Shared in-flight authorizeSubmit() promise. Cleared as soon as it settles —
   * permits / warn_allow are one-time and are never cached for reuse.
   */
  private authorizeInFlight: Promise<SubmitAuthorization> | null = null;
  /** Last runtime bootstrap input — reused to refresh a missing/stale proof. */
  private lastRuntimeBootstrapInput: RuntimeBootstrapInput | null = null;

  constructor(config: ColorShieldConfig) {
    this.config = config;
  }

  getState(): AuthState {
    return this.state;
  }

  getSubmitGateState(): SubmitGateState {
    return this.submitGateState;
  }

  /**
   * Visual Trust Signal state — derived only from runtime trust + submit gate.
   * Not authoritative for submission. No public setter exists.
   */
  getVisualTrustState(): VisualTrustState {
    return deriveVisualTrustState(this.visualTrustInputs());
  }

  getVisualTrustDetail(): VisualTrustDetail {
    return buildVisualTrustDetail(this.visualTrustInputs());
  }

  /** Test helper: whether the mounted visual DOM currently contains secret material. */
  visualTrustContainsSecrets(): boolean {
    if (!this.trustSignalEl) return false;
    return visualTrustDomContainsSecrets(this.trustSignalEl);
  }

  /**
   * Server-provisioned policy is authoritative.
   * policy_mode / policyMode normalize to uppercase; default empty.
   */
  private effectivePolicyMode(): string {
    return String(this.config.policyMode || "").trim().toUpperCase();
  }

  private isBlockPolicy(): boolean {
    return this.effectivePolicyMode() === "BLOCK";
  }

  private isWarnPolicy(): boolean {
    return this.effectivePolicyMode() === "WARN";
  }

  /**
   * ColorShield form_context_id (runtime bootstrap / pre-submit / expected_form_id).
   * `formId` stays the DOM-side identifier; falls back to it for older integrations.
   */
  private formContextId(): string {
    return this.config.formContextId ?? this.config.formId;
  }

  /** Runtime-trust integration: start() bootstraps live proof instead of legacy verify. */
  private usesRuntimeTrustStart(): boolean {
    return Boolean(this.config.runtimeBootstrapUrl);
  }

  /**
   * Controlled submit path (Hard Block OR AuditGuard WARN).
   * BLOCK policy ALWAYS uses this path — hardBlock=false cannot override.
   * Modern provisioned flows (flowKey present) also use this path unless
   * explicitly marked legacyAuthenticitySubmit (not allowed to weaken BLOCK).
   */
  private shouldUseHardBlock(): boolean {
    if (this.isBlockPolicy()) return true;
    if (this.isWarnPolicy()) return true;
    if (this.config.hardBlock === true) return true;
    if (this.config.flowKey && this.config.legacyAuthenticitySubmit !== true) return true;
    return false;
  }

  /**
   * Legacy authenticity-token submit — compatibility only.
   * NOT the default for modern provisioned / BLOCK flows.
   * Selected only when Hard Block does not apply AND:
   *   - legacyAuthenticitySubmit === true, OR
   *   - classic demo shape: no flowKey, no policyMode, no preSubmitUrl
   */
  private shouldUseLegacyAuthenticity(): boolean {
    if (this.shouldUseHardBlock()) return false;
    if (this.config.legacyAuthenticitySubmit === true) return true;
    if (!this.config.flowKey && !this.config.policyMode && !this.config.preSubmitUrl) {
      return true;
    }
    return false;
  }

  getRuntimeTrustState(): RuntimeTrustState {
    this.refreshRuntimeProofExpiry();
    return this.runtimeTrustState;
  }

  /** Apply proof expiry using engine timestamps — no separate UI TTL. */
  private refreshRuntimeProofExpiry(): boolean {
    if (
      this.runtimeTrustState === "VERIFIED" &&
      this.runtimeProofExpiresAtMs &&
      this.now() >= this.runtimeProofExpiresAtMs
    ) {
      this.runtimeTrustState = "STALE";
      this.runtimeProof = null;
      return true;
    }
    return false;
  }

  /**
   * Same-origin live proof bootstrap (installation credential stays on customer server).
   * Does not transmit form field contents. Does not implement Hard Block yet.
   */
  async bootstrapRuntimeTrust(input: RuntimeBootstrapInput): Promise<RuntimeTrustState> {
    this.lastRuntimeBootstrapInput = { ...input };
    this.runtimeTrustState = "BOOTSTRAPPING";
    this.renderTrustSignal();
    this.browserNonce = generateBrowserNonce();
    const loc = this.currentLocation();
    try {
      const fetchImpl = this.config.fetchImpl || fetch;
      const res = await fetchImpl(input.bootstrapUrl, {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({
          flow_key: input.flowKey,
          browser_nonce: this.browserNonce,
          hostname: input.hostname || loc.hostname,
          path: input.path || loc.pathname,
          form_context_id: input.formContextId,
          config_version: input.configVersion,
        }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        runtime_proof?: string;
        expires_at?: string;
      };
      if (!res.ok || !data.ok || !data.runtime_proof) {
        this.runtimeTrustState = "FAILED";
        this.syncRuntimeAuthState();
        this.renderTrustSignal();
        return this.runtimeTrustState;
      }
      this.runtimeProof = data.runtime_proof;
      this.runtimeProofExpiresAtMs = data.expires_at
        ? Date.parse(data.expires_at)
        : this.now() + 60_000;
      this.lastRuntimeVerifiedAtMs = this.now();
      this.runtimeTrustState = "VERIFIED";
      this.syncRuntimeAuthState();
      this.renderTrustSignal();
      return this.runtimeTrustState;
    } catch {
      this.runtimeTrustState = "FAILED";
      this.syncRuntimeAuthState();
      this.renderTrustSignal();
      return this.runtimeTrustState;
    }
  }

  getDetail(): StateDetail {
    return {
      state: this.state,
      verdict: this.lastVerdict || undefined,
      reason_code: this.lastReason || undefined,
      message: STATE_MESSAGES[this.state],
      expires_at: this.tokenExpiresAtMs ? new Date(this.tokenExpiresAtMs).toISOString() : undefined,
      token_present: Boolean(this.token),
    };
  }

  /** Masked runtime snapshot for demo UI — no secrets/full tokens. */
  getDebugSnapshot(): Record<string, unknown> {
    return {
      state: this.state,
      visual_trust: this.getVisualTrustState(),
      runtime_trust: this.getRuntimeTrustState(),
      submit_gate: this.submitGateState,
      verdict: this.lastVerdict,
      reason_code: this.lastReason,
      domain: this.currentLocation().hostname,
      path: this.currentLocation().pathname,
      formId: this.config.formId,
      form_context_id: this.formContextId(),
      session_id: this.bootstrap?.session_id || this.config.sessionId || null,
      token_present: Boolean(this.token),
      token_expires_at: this.tokenExpiresAtMs ? new Date(this.tokenExpiresAtMs).toISOString() : null,
      runtime_proof_present: Boolean(this.runtimeProof),
      runtime_proof_expires_at: this.runtimeProofExpiresAtMs
        ? new Date(this.runtimeProofExpiresAtMs).toISOString()
        : null,
      bootstrap_present: Boolean(this.bootstrap?.bootstrap_assertion),
      assertion_preview: this.mask(this.bootstrap?.bootstrap_assertion),
      token_preview: this.mask(this.token),
    };
  }

  async start(): Promise<void> {
    this.bindDom();
    this.setState("INITIALIZING");
    this.renderTrustSignal();
    if (this.usesRuntimeTrustStart()) {
      // Runtime-trust integration: the visual signal follows the live runtime proof.
      // The legacy authenticity bootstrap/verify loop is not used on this path.
      await this.startRuntimeTrust();
    } else if (this.config.skipAuthenticityVerify !== true) {
      await this.runFullVerification();
    }
    this.startRefreshLoop();
  }

  private async startRuntimeTrust(): Promise<void> {
    const input = this.runtimeBootstrapInput();
    if (!input) {
      // runtimeBootstrapUrl without flowKey — cannot obtain a proof; never shown as VERIFIED.
      this.runtimeTrustState = "FAILED";
      this.syncRuntimeAuthState();
      this.renderTrustSignal();
      return;
    }
    await this.bootstrapRuntimeTrust(input);
  }

  /**
   * Runtime-trust integration only: mirror runtime trust into AuthState so
   * getState() / the indicator agree with the Visual Trust Signal.
   * VERIFIED only with a live runtime proof; never overrides BLOCKED.
   */
  private syncRuntimeAuthState(): void {
    if (!this.usesRuntimeTrustStart() || this.destroyed) return;
    if (this.state === "BLOCKED") return;
    const rt = this.runtimeTrustState;
    const next: AuthState | null =
      rt === "VERIFIED"
        ? "VERIFIED"
        : rt === "FAILED"
          ? "OFFLINE"
          : rt === "STALE"
            ? "EXPIRED"
            : null;
    if (next && next !== this.state) {
      this.setState(next, rt === "VERIFIED" ? "RUNTIME_PROOF_VALID" : `RUNTIME_${rt}`);
    }
  }

  destroy(): void {
    this.destroyed = true;
    // Drop shared authorization state — nothing may be reused after destroy.
    this.authorizeInFlight = null;
    this.runtimeProof = null;
    this.runtimeProofExpiresAtMs = 0;
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    if (this.formEl && this.submitHandler) {
      this.formEl.removeEventListener("submit", this.submitHandler, true);
    }
    if (this.trustSignalEl && this.trustAutoMounted) {
      this.trustSignalEl.remove();
    }
    this.trustSignalEl = null;
  }

  /** Force re-check (used by tests and before submit). */
  async reverify(): Promise<AuthState> {
    await this.runFullVerification();
    return this.state;
  }

  /**
   * Returns the current authenticity token only when VERIFIED and not expired.
   * Does not expose secrets or signing keys. Token is single-use once submitted.
   */
  getSubmissionToken(): string | null {
    if (this.destroyed) return null;
    if (this.state !== "VERIFIED") return null;
    if (!this.token) return null;
    if (this.now() >= this.tokenExpiresAtMs) return null;
    if (this.contextDrifted()) return null;
    return this.token;
  }

  /**
   * Returns true only when submit may proceed.
   * Fail-closed: any doubt => false.
   */
  async ensureCanSubmit(): Promise<boolean> {
    if (this.destroyed) return false;
    if (this.contextDrifted()) {
      this.timeline("CONTEXT_INVALIDATED", { reason: "PAGE_OR_FORM_CONTEXT_CHANGED" });
      this.clearAuth("BLOCKED", "CONTEXT_CHANGED");
      await this.runFullVerification();
    }

    const refreshSkew = (this.config.refreshBeforeExpirySeconds ?? 10) * 1000;
    const now = this.now();
    if (!this.token || this.state !== "VERIFIED" || now >= this.tokenExpiresAtMs - refreshSkew) {
      if (this.token && now >= this.tokenExpiresAtMs) {
        this.timeline("TOKEN_EXPIRED");
        this.setState("EXPIRED", "TOKEN_EXPIRED");
      }
      await this.runFullVerification();
    }

    const ok =
      this.state === "VERIFIED" &&
      Boolean(this.token) &&
      this.now() < this.tokenExpiresAtMs &&
      !this.contextDrifted();

    if (!ok) {
      this.setState("BLOCKED", this.lastReason || "SUBMIT_DENIED");
      this.timeline("SUBMIT_BLOCKED", { reason_code: this.lastReason || "SUBMIT_DENIED" });
      return false;
    }
    this.timeline("SUBMIT_ALLOWED");
    return true;
  }

  private bindDom(): void {
    const doc = this.doc();
    if (!doc) return;
    this.formEl = doc.querySelector(this.config.protectedFormSelector);
    this.indicatorEl = doc.querySelector(this.config.indicatorSelector);
    this.mountTrustSignal(doc);
    if (!this.formEl) {
      this.setState("BLOCKED", "FORM_NOT_FOUND");
      return;
    }
    if (this.config.interceptSubmit === false) {
      // Customer handler stays authoritative; it calls authorizeSubmit() itself.
      this.renderIndicator();
      this.renderTrustSignal();
      return;
    }
    this.submitHandler = (ev: Event) => {
      // One-shot release after PERMIT for native submit path — avoid recursion.
      if (this.allowNativeSubmitOnce) {
        this.allowNativeSubmitOnce = false;
        return;
      }
      // Capture before native submission starts.
      ev.preventDefault();
      ev.stopPropagation();
      if (typeof (ev as Event & { stopImmediatePropagation?: () => void }).stopImmediatePropagation === "function") {
        (ev as Event & { stopImmediatePropagation: () => void }).stopImmediatePropagation();
      }
      void this.onSubmitAttempt(ev);
    };
    // Capture phase — covers button click and Enter-key submit.
    this.formEl.addEventListener("submit", this.submitHandler, true);
    this.renderIndicator();
    this.renderTrustSignal();
  }

  private mountTrustSignal(doc: Document): void {
    if (this.config.trustSignalEnabled === false) return;
    const host = resolveTrustMountHost({
      document: doc,
      formEl: this.formEl,
      selector: this.config.trustSignalSelector,
      formId: this.config.formId,
      instanceId: this.instanceId,
    });
    if (!host) return;
    this.trustAutoMounted = host.getAttribute("data-csid-auto-mount") === "1";
    this.trustSignalEl = host;
  }

  private doc(): Document | null {
    if (this.config.document) return this.config.document;
    if (typeof document !== "undefined") return document;
    return null;
  }

  private visualTrustInputs() {
    // Refresh expiry before reading — uses engine TTL, not a UI hardcode.
    const runtimeTrustState = this.getRuntimeTrustState();
    return {
      runtimeTrustState,
      submitGateState: this.submitGateState,
      authBlocked: this.state === "BLOCKED",
      lastVerifiedAt: this.lastRuntimeVerifiedAtMs
        ? new Date(this.lastRuntimeVerifiedAtMs).toISOString()
        : null,
      hostname: this.currentLocation().hostname,
      flowType: this.config.flowType || null,
      policyMode: this.effectivePolicyMode() || null,
    };
  }

  /**
   * Visual Trust Signal render.
   * Reflects engine state only — never authorizes submit or issues tokens.
   */
  private renderTrustSignal(): void {
    if (this.destroyed) return;
    if (!this.trustSignalEl) {
      const doc = this.doc();
      if (doc && this.config.trustSignalEnabled !== false) {
        this.mountTrustSignal(doc);
      }
    }
    const detail = buildVisualTrustDetail(this.visualTrustInputs());
    if (this.trustSignalEl) {
      renderVisualTrustSignal(this.trustSignalEl, detail, { instanceId: this.instanceId });
    }
    if (this.lastVisualState !== detail.state) {
      this.lastVisualState = detail.state;
      this.config.onVisualTrustChange?.(detail.state, detail);
    }
  }

  private async onSubmitAttempt(_ev: Event): Promise<void> {
    if (this.shouldUseHardBlock()) {
      await this.onHardBlockSubmit();
      return;
    }
    if (this.shouldUseLegacyAuthenticity()) {
      await this.onLegacyAuthenticitySubmit();
      return;
    }
    // Unknown modern configuration without a valid Hard Block path — fail closed.
    this.submitGateState = "BLOCKED";
    this.setState("BLOCKED", "POLICY_BLOCK");
    this.timeline("SUBMIT_BLOCKED", { reason_code: "POLICY_BLOCK" });
  }

  /**
   * Controlled submit (BLOCK Hard Block / WARN AuditGuard).
   * preventDefault already applied.
   * BLOCK: fail-closed — never allow without PERMIT; network failure blocks.
   * WARN: PERMIT on success; WARN_ALLOW on reachable validation failure;
   *       WARN_UNAVAILABLE if ColorShield unreachable (explicit WARNING, not VERIFIED).
   * Never falls back to legacy authenticity. Does not transmit form fields to ColorShield.
   */
  private async onHardBlockSubmit(): Promise<void> {
    const resultEl = typeof document !== "undefined" ? document.getElementById("cs-demo-result") : null;
    if (this.submitInFlight || this.submitGateState === "VALIDATING") {
      return;
    }
    this.submitInFlight = true;
    this.submitGateState = "VALIDATING";
    this.renderTrustSignal();
    const warnMode = this.isWarnPolicy();

    const finishBlock = (reason: string) => {
      this.submitGateState = "BLOCKED";
      this.submitInFlight = false;
      this.setState("BLOCKED", reason);
      this.timeline("PRE_SUBMIT_BLOCKED", { reason_code: reason });
      this.timeline("SUBMIT_BLOCKED", { reason_code: reason });
      this.renderIndicator();
      this.renderTrustSignal();
      if (resultEl) {
        resultEl.textContent = "Submission blocked: ColorShield validation did not permit submit.";
        resultEl.className = "cs-demo-result bad";
      }
    };

    const finishError = (reason: string) => {
      this.submitGateState = "ERROR";
      this.submitInFlight = false;
      this.setState("BLOCKED", reason);
      this.timeline("PRE_SUBMIT_BLOCKED", { reason_code: reason });
      this.renderIndicator();
      this.renderTrustSignal();
      if (resultEl) {
        resultEl.textContent = "Submission blocked: ColorShield validation unavailable.";
        resultEl.className = "cs-demo-result bad";
      }
    };

    const postCustomerSubmit = async (
      fetchImpl: typeof fetch,
      loc: { hostname: string; pathname: string },
      auth: Record<string, unknown>,
      warningLabel?: string
    ): Promise<boolean> => {
      const submitUrl = this.config.protectedSubmitUrl || "/demo/protected-submit";
      const body: Record<string, unknown> = {
        ...auth,
        expected_domain: loc.hostname,
        expected_path: loc.pathname,
        expected_form_id: this.formContextId(),
        policy_mode: this.effectivePolicyMode() || undefined,
      };
      if (this.formEl) {
        const emailInput = this.formEl.querySelector<HTMLInputElement>(
          'input[name="email"], input[type="email"]'
        );
        const passwordInput = this.formEl.querySelector<HTMLInputElement>(
          'input[name="password"], input[type="password"]'
        );
        if (emailInput?.value) body.email = emailInput.value;
        if (passwordInput?.value) body.password = passwordInput.value;
      }
      const submitRes = await fetchImpl(submitUrl, {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify(body),
      });
      const submitData = (await submitRes.json()) as {
        ok?: boolean;
        message?: string;
        reason_code?: string;
      };
      this.runtimeProof = null;
      this.runtimeProofExpiresAtMs = 0;
      this.runtimeTrustState = "STALE";
      this.renderTrustSignal();
      if (submitRes.ok && submitData.ok) {
        this.submitGateState = "IDLE";
        this.submitInFlight = false;
        this.renderTrustSignal();
        this.timeline("PROTECTED_SUBMIT_ACCEPTED", {
          reason_code: submitData.reason_code,
          warning: Boolean(warningLabel),
        });
        if (resultEl) {
          resultEl.textContent =
            submitData.message ||
            (warningLabel
              ? `Submission accepted with ColorShield warning (${warningLabel}).`
              : "Protected submission accepted after ColorShield permit verification.");
          resultEl.className = warningLabel ? "cs-demo-result warn" : "cs-demo-result ok";
        }
        this.formEl?.reset();
        return true;
      }
      return false;
    };

    try {
      this.getRuntimeTrustState();
      const loc = this.currentLocation();
      const preSubmitUrl = this.config.preSubmitUrl;
      const fetchImpl = this.config.fetchImpl || fetch;

      if (!preSubmitUrl) {
        if (warnMode) {
          this.submitGateState = "WARNING";
          this.renderTrustSignal();
          this.timeline("WARN_TRANSPORT_UNAVAILABLE", { reason_code: "RUNTIME_UNAVAILABLE" });
          const ok = await postCustomerSubmit(
            fetchImpl,
            loc,
            { warn_transport_unavailable: true, policy_mode: "WARN" },
            "RUNTIME_UNAVAILABLE"
          );
          if (!ok) finishBlock("SUBMIT_REJECTED");
          else this.submitInFlight = false;
          return;
        }
        finishBlock("RUNTIME_PROOF_REQUIRED");
        return;
      }

      const timeoutMs = this.config.preSubmitTimeoutMs ?? DEFAULT_PRE_SUBMIT_TIMEOUT_MS;
      const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

      this.timeline("PRE_SUBMIT_REQUESTED");
      let res: Response;
      try {
        res = await fetchImpl(preSubmitUrl, {
          method: "POST",
          headers: { "content-type": "application/json", accept: "application/json" },
          signal: controller?.signal,
          body: JSON.stringify({
            runtime_proof: this.runtimeProof || "",
            browser_nonce: this.browserNonce || "",
            hostname: loc.hostname,
            path: loc.pathname,
            form_context_id: this.formContextId(),
            flow_key: this.config.flowKey || "",
          }),
        });
      } catch {
        if (timer) clearTimeout(timer);
        if (warnMode) {
          this.submitGateState = "WARNING";
          this.renderTrustSignal();
          this.timeline("WARN_TRANSPORT_UNAVAILABLE", { reason_code: "RUNTIME_UNAVAILABLE" });
          const ok = await postCustomerSubmit(
            fetchImpl,
            loc,
            { warn_transport_unavailable: true, policy_mode: "WARN" },
            "RUNTIME_UNAVAILABLE"
          );
          if (!ok) finishBlock("SUBMIT_REJECTED");
          else this.submitInFlight = false;
          return;
        }
        finishError("SUBMIT_TIMEOUT");
        return;
      }
      if (timer) clearTimeout(timer);

      const data = (await res.json()) as {
        decision?: string;
        reason_code?: string;
        submission_permit?: string;
        warn_allow?: string;
        warning_reason?: string;
      };

      if (data.decision === "PERMIT" && data.submission_permit) {
        this.submitGateState = "PERMITTED";
        this.renderTrustSignal();
        this.timeline("PRE_SUBMIT_PERMITTED", { reason_code: data.reason_code });
        this.timeline("SUBMIT_ALLOWED");
        const ok = await postCustomerSubmit(fetchImpl, loc, {
          submission_permit: data.submission_permit,
        });
        if (!ok) finishBlock("SUBMIT_REJECTED");
        return;
      }

      if (warnMode && data.decision === "WARN_ALLOW" && data.warn_allow) {
        this.submitGateState = "WARNING";
        this.renderTrustSignal();
        this.timeline("WARN_SUBMISSION_ALLOWED", {
          reason_code: data.warning_reason || data.reason_code,
        });
        const ok = await postCustomerSubmit(
          fetchImpl,
          loc,
          {
            warn_allow: data.warn_allow,
            policy_mode: "WARN",
            warning_reason: data.warning_reason || data.reason_code,
          },
          data.warning_reason || data.reason_code || "WARN"
        );
        if (!ok) finishBlock("SUBMIT_REJECTED");
        return;
      }

      finishBlock(data.reason_code || "POLICY_BLOCK");
    } catch {
      if (warnMode) {
        this.submitGateState = "WARNING";
        this.renderTrustSignal();
        this.timeline("WARN_TRANSPORT_UNAVAILABLE", { reason_code: "RUNTIME_UNAVAILABLE" });
        try {
          const fetchImpl = this.config.fetchImpl || fetch;
          const loc = this.currentLocation();
          const ok = await postCustomerSubmit(
            fetchImpl,
            loc,
            { warn_transport_unavailable: true, policy_mode: "WARN" },
            "RUNTIME_UNAVAILABLE"
          );
          if (!ok) finishBlock("SUBMIT_REJECTED");
          else this.submitInFlight = false;
          return;
        } catch {
          finishError("INTERNAL_VALIDATION_ERROR");
          return;
        }
      }
      finishError("INTERNAL_VALIDATION_ERROR");
    }
  }

  /**
   * Obtain submit authorization for the customer's OWN submit handler
   * (used with interceptSubmit: false). Does not submit anything, does not
   * read or transmit form fields, and never throws.
   *
   * - Concurrent calls share one in-flight pre-submit request.
   * - The shared promise is cleared once settled; permits are never cached,
   *   so every later submit performs a new pre-submit request.
   * - BLOCK (and any non-WARN policy): fail-closed — allowed only on PERMIT.
   * - WARN: fail-open — PERMIT, WARN_ALLOW, WARN_REFUSED (explicit Control
   *   Tower refusal) or WARN_TRANSPORT_UNAVAILABLE (Control Tower unusable);
   *   always carries explicit metadata, never allowed=false.
   */
  authorizeSubmit(): Promise<SubmitAuthorization> {
    if (this.authorizeInFlight) return this.authorizeInFlight;
    const pending = this.runAuthorizeSubmit();
    this.authorizeInFlight = pending;
    const clear = () => {
      if (this.authorizeInFlight === pending) this.authorizeInFlight = null;
    };
    pending.then(clear, clear);
    return pending;
  }

  private async runAuthorizeSubmit(): Promise<SubmitAuthorization> {
    const warnMode = this.isWarnPolicy();
    this.submitGateState = "VALIDATING";
    this.timeline("SUBMIT_AUTHORIZATION_REQUESTED");
    this.renderTrustSignal();
    let result: SubmitAuthorization;
    try {
      result = await this.requestSubmitAuthorization(warnMode);
    } catch {
      result = warnMode
        ? this.warnTransportUnavailable("INTERNAL_VALIDATION_ERROR")
        : this.blockAuthorization("INTERNAL_VALIDATION_ERROR");
    }
    // Destroyed mid-flight: never hand out a permit / warn_allow afterwards.
    if (this.destroyed && result.decision !== "BLOCK" && result.reason_code !== "CLIENT_DESTROYED") {
      result = warnMode
        ? this.warnTransportUnavailable("CLIENT_DESTROYED")
        : this.blockAuthorization("CLIENT_DESTROYED");
    }
    this.submitGateState =
      result.decision === "BLOCK"
        ? "BLOCKED"
        : result.decision === "PERMIT"
          ? "IDLE"
          : "WARNING";
    this.timeline("SUBMIT_AUTHORIZATION_RESOLVED", {
      decision: result.decision,
      reason_code: result.reason_code,
    });
    this.renderTrustSignal();
    return result;
  }

  /**
   * Outcome classification (WARN policy):
   *   PERMIT                     — 2xx PERMIT with submission_permit
   *   WARN_ALLOW                 — 2xx WARN_ALLOW with warn_allow
   *   WARN_REFUSED               — Control Tower answered with a refusal: a 4xx
   *                                (structured reason_code, else HTTP_<status>)
   *                                or a structured BLOCK decision
   *   WARN_TRANSPORT_UNAVAILABLE — Control Tower not usable: timeout, network /
   *                                connection failure, 5xx, unusable response,
   *                                or no pre-submit endpoint / client available
   * Non-WARN policy: anything but PERMIT → BLOCK (fail-closed).
   */
  private async requestSubmitAuthorization(warnMode: boolean): Promise<SubmitAuthorization> {
    const unavailable = (reason: string) =>
      warnMode ? this.warnTransportUnavailable(reason) : this.blockAuthorization(reason);
    const refused = (reason: string) =>
      warnMode ? this.warnRefused(reason) : this.blockAuthorization(reason);

    if (this.destroyed) return unavailable("CLIENT_DESTROYED");
    const preSubmitUrl = this.config.preSubmitUrl;
    if (!preSubmitUrl) return unavailable("RUNTIME_UNAVAILABLE");

    // No proof and no way to obtain one: resolve locally — never send an empty runtime_proof.
    this.refreshRuntimeProofExpiry();
    if (!this.runtimeProof && !this.runtimeBootstrapInput()) {
      if (!warnMode) this.timeline("PRE_SUBMIT_BLOCKED", { reason_code: "RUNTIME_UNAVAILABLE" });
      return unavailable("RUNTIME_UNAVAILABLE");
    }

    await this.ensureFreshRuntimeProof();
    if (this.destroyed) return unavailable("CLIENT_DESTROYED");
    // Bootstrap could not produce a proof — resolve locally, never send an empty runtime_proof.
    if (!this.runtimeProof) {
      if (!warnMode) this.timeline("PRE_SUBMIT_BLOCKED", { reason_code: "RUNTIME_UNAVAILABLE" });
      return unavailable("RUNTIME_UNAVAILABLE");
    }

    const loc = this.currentLocation();
    const fetchImpl = this.config.fetchImpl || fetch;
    const timeoutMs = this.config.preSubmitTimeoutMs ?? DEFAULT_PRE_SUBMIT_TIMEOUT_MS;
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

    this.timeline("PRE_SUBMIT_REQUESTED");
    let res: Response;
    try {
      res = await fetchImpl(preSubmitUrl, {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        signal: controller?.signal,
        body: JSON.stringify(this.preSubmitBody(loc)),
      });
    } catch {
      // Timeout (abort) or network / connection failure.
      return unavailable("RUNTIME_UNAVAILABLE");
    } finally {
      if (timer) clearTimeout(timer);
    }

    // The proof was presented — never reuse it; the next authorization re-bootstraps.
    this.invalidateRuntimeProof();

    let data: {
      ok?: boolean;
      decision?: string;
      reason_code?: string;
      submission_permit?: string;
      warn_allow?: string;
      warning_reason?: string;
    } | null;
    try {
      const parsed = (await res.json()) as unknown;
      data = parsed && typeof parsed === "object" ? (parsed as NonNullable<typeof data>) : null;
    } catch {
      data = null;
    }
    const reasonCode = data && typeof data.reason_code === "string" && data.reason_code ? data.reason_code : "";

    // 5xx — service unavailable, never treated as a refusal.
    if (res.status >= 500) {
      const reason = reasonCode || `HTTP_${res.status}`;
      if (!warnMode) this.timeline("PRE_SUBMIT_BLOCKED", { reason_code: reason });
      return unavailable(reason);
    }

    if (res.ok && data?.decision === "PERMIT" && data.submission_permit) {
      this.timeline("PRE_SUBMIT_PERMITTED", { reason_code: data.reason_code });
      return this.authorization(true, "PERMIT", reasonCode || "OK_CONTEXT_VALID", {
        submission_permit: data.submission_permit,
      });
    }

    if (warnMode && res.ok && data?.decision === "WARN_ALLOW" && data.warn_allow) {
      const warningReason = data.warning_reason || reasonCode || "WARN";
      this.timeline("WARN_SUBMISSION_ALLOWED", { reason_code: warningReason });
      return this.authorization(true, "WARN_ALLOW", warningReason, {
        warn_allow: data.warn_allow,
        policy_mode: "WARN",
        warning_reason: warningReason,
      });
    }

    // Explicit refusal: any 4xx, or a structured BLOCK decision.
    const isClientError = res.status >= 400 && res.status < 500;
    const structuredBlock = data?.decision === "BLOCK" && Boolean(reasonCode);
    if (isClientError || structuredBlock) {
      const reason = reasonCode || `HTTP_${res.status}`;
      if (!warnMode) this.timeline("PRE_SUBMIT_BLOCKED", { reason_code: reason });
      return refused(reason);
    }

    // 2xx/3xx without a usable decision — response unusable, not a refusal.
    if (!warnMode) this.timeline("PRE_SUBMIT_BLOCKED", { reason_code: reasonCode || "INVALID_RESPONSE" });
    return unavailable(reasonCode || "INVALID_RESPONSE");
  }

  /** Refresh the runtime proof when missing or stale (no-op without bootstrap input). */
  private async ensureFreshRuntimeProof(): Promise<void> {
    this.refreshRuntimeProofExpiry();
    if (this.runtimeProof && this.runtimeTrustState === "VERIFIED") return;
    const input = this.runtimeBootstrapInput();
    if (!input) return;
    await this.bootstrapRuntimeTrust(input);
  }

  private runtimeBootstrapInput(): RuntimeBootstrapInput | null {
    if (this.lastRuntimeBootstrapInput) return this.lastRuntimeBootstrapInput;
    const { runtimeBootstrapUrl, flowKey } = this.config;
    if (!runtimeBootstrapUrl || !flowKey) return null;
    return {
      bootstrapUrl: runtimeBootstrapUrl,
      flowKey,
      formContextId: this.formContextId(),
      configVersion: this.config.configVersion ?? 1,
    };
  }

  /** Pre-submit request body — ColorShield context only, never form field contents. */
  private preSubmitBody(loc: { hostname: string; pathname: string }): Record<string, string> {
    return {
      runtime_proof: this.runtimeProof || "",
      browser_nonce: this.browserNonce || "",
      hostname: loc.hostname,
      path: loc.pathname,
      form_context_id: this.formContextId(),
      flow_key: this.config.flowKey || "",
    };
  }

  private invalidateRuntimeProof(): void {
    this.runtimeProof = null;
    this.runtimeProofExpiresAtMs = 0;
    this.runtimeTrustState = "STALE";
  }

  private warnTransportUnavailable(reason: string): SubmitAuthorization {
    this.timeline("WARN_TRANSPORT_UNAVAILABLE", { reason_code: reason });
    return this.authorization(true, "WARN_TRANSPORT_UNAVAILABLE", reason, {
      warn_transport_unavailable: "true",
      policy_mode: "WARN",
    });
  }

  private warnRefused(reason: string): SubmitAuthorization {
    this.timeline("WARN_AUTHORIZATION_REFUSED", { reason_code: reason });
    return this.authorization(true, "WARN_REFUSED", reason, {
      warn_authorization_refused: "true",
      policy_mode: "WARN",
      warning_reason: reason,
    });
  }

  private blockAuthorization(reason: string): SubmitAuthorization {
    return this.authorization(false, "BLOCK", reason, {});
  }

  private authorization(
    allowed: boolean,
    decision: SubmitAuthorizationDecision,
    reason_code: string,
    fields: Record<string, string>
  ): SubmitAuthorization {
    return Object.freeze({ allowed, decision, reason_code, fields: Object.freeze({ ...fields }) });
  }

  /**
   * Legacy authenticity-token submit path (older demo / explicit compatibility only).
   * Boundary: selected only via shouldUseLegacyAuthenticity() — never as a silent
   * fallback for policyMode=BLOCK or modern provisioned flows (flowKey).
   */
  private async onLegacyAuthenticitySubmit(): Promise<void> {
    if (this.submitInFlight) return;
    this.submitInFlight = true;
    const resultEl = typeof document !== "undefined" ? document.getElementById("cs-demo-result") : null;
    try {
      const allowed = await this.ensureCanSubmit();
      if (!allowed) {
        this.submitGateState = "BLOCKED";
        this.renderIndicator();
        if (resultEl) {
          resultEl.textContent = "Submission rejected: authenticity verification failed.";
          resultEl.className = "cs-demo-result bad";
        }
        return;
      }

      const token = this.getSubmissionToken();
      if (!token) {
        this.setState("BLOCKED", "TOKEN_MISSING");
        this.submitGateState = "BLOCKED";
        this.timeline("SUBMIT_BLOCKED", { reason_code: "TOKEN_MISSING" });
        this.renderIndicator();
        if (resultEl) {
          resultEl.textContent = "Submission rejected: authenticity verification failed.";
          resultEl.className = "cs-demo-result bad";
        }
        return;
      }

      const loc = this.currentLocation();
      const submitUrl = this.config.protectedSubmitUrl || "/demo/protected-submit";
      const fetchImpl = this.config.fetchImpl || fetch;

      const body: Record<string, unknown> = {
        authenticity_token: token,
        expected_session_id: this.verifiedSessionId || this.bootstrap?.session_id || "",
        expected_domain: loc.hostname,
        expected_path: loc.pathname,
        expected_form_id: this.formContextId(),
      };
      if (this.formEl) {
        const emailInput = this.formEl.querySelector<HTMLInputElement>('input[name="email"], input[type="email"]');
        const passwordInput = this.formEl.querySelector<HTMLInputElement>('input[name="password"], input[type="password"]');
        if (emailInput?.value) body.email = emailInput.value;
        if (passwordInput?.value) body.password = passwordInput.value;
      }

      const res = await fetchImpl(submitUrl, {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify(body),
      });
      const data = (await res.json()) as { ok?: boolean; message?: string; reason_code?: string };

      this.token = null;
      this.tokenExpiresAtMs = 0;

      if (res.ok && data.ok) {
        this.submitGateState = "IDLE";
        this.timeline("PROTECTED_SUBMIT_ACCEPTED", { reason_code: data.reason_code });
        if (resultEl) {
          resultEl.textContent =
            data.message || "Protected submission accepted after server-side ColorShield verification.";
          resultEl.className = "cs-demo-result ok";
        }
        this.formEl?.reset();
        await this.runFullVerification();
        return;
      }

      this.submitGateState = "BLOCKED";
      this.timeline("PROTECTED_SUBMIT_REJECTED", { reason_code: data.reason_code || "SUBMIT_REJECTED" });
      this.setState("BLOCKED", data.reason_code || "SUBMIT_REJECTED");
      if (resultEl) {
        resultEl.textContent =
          data.message || "Submission rejected: authenticity verification failed.";
        resultEl.className = "cs-demo-result bad";
      }
      this.renderIndicator();
    } catch {
      this.token = null;
      this.tokenExpiresAtMs = 0;
      this.submitGateState = "ERROR";
      this.timeline("PROTECTED_SUBMIT_REJECTED", { reason_code: "TOWER_OR_BACKEND_UNREACHABLE" });
      this.clearAuth("OFFLINE", "TOWER_OR_BACKEND_UNREACHABLE");
      if (resultEl) {
        resultEl.textContent = "Submission rejected: authenticity verification failed.";
        resultEl.className = "cs-demo-result bad";
      }
    } finally {
      this.submitInFlight = false;
    }
  }

  private async runFullVerification(): Promise<void> {
    try {
      const scenario = this.activeScenario();
      if (scenario === "CONTROL_TOWER_OFFLINE_SIMULATION") {
        this.clearAuth("OFFLINE", "TOWER_OFFLINE_SIMULATION");
        return;
      }

      this.timeline("BOOTSTRAP_REQUESTED");
      const boot = await this.requestBootstrap(scenario);
      this.bootstrap = boot;
      this.timeline("BOOTSTRAP_RECEIVED", {
        session_id: boot.session_id,
        expires_at: boot.expires_at,
        assertion_preview: this.mask(boot.bootstrap_assertion),
      });

      if (scenario === "EXPIRED_ASSERTION_SIMULATION") {
        // Wait past bootstrap TTL is too slow for demos; mark expired locally and force verify with stale claim by delaying verify clock is hard.
        // Instead: verify once (consumes nonce), then attempt verify again => NONCE_REUSED mapped; for expired we call verify with assertion after clearing and using a fake expired path:
        // Practical demo: set state EXPIRED without calling verify successfully.
        this.clearAuth("EXPIRED", "ASSERTION_EXPIRED_SIMULATION");
        this.timeline("TOKEN_EXPIRED", { simulated: true });
        return;
      }

      if (scenario === "REUSED_NONCE_SIMULATION") {
        await this.callVerify(boot, "VALID");
        const second = await this.callVerify(boot, "VALID");
        this.applyVerifyResult(second);
        return;
      }

      const result = await this.callVerify(boot, scenario);
      this.applyVerifyResult(result);
    } catch {
      this.clearAuth("OFFLINE", "NETWORK_OR_TOWER_ERROR");
    }
  }

  private async requestBootstrap(scenario: DemoScenario | null): Promise<BootstrapPayload> {
    const loc = this.currentLocation();
    const session_id =
      this.config.sessionId ||
      `sess_${Math.random().toString(16).slice(2)}_${this.now().toString(36)}`;

    // Bootstrap always uses real expected context for VALID tenant allowlist.
    // Mismatch scenarios alter VERIFY payload only (except offline).
    const body: Record<string, unknown> = {
      session_id,
      expected_domain: loc.hostname,
      expected_path: loc.pathname,
      form_id: this.formContextId(),
    };
    if (this.config.allowDemoScenarios && scenario) {
      body.demo_scenario = scenario;
    }

    const fetchImpl = this.config.fetchImpl || fetch;
    const res = await fetchImpl(this.config.bootstrapUrl, {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
    });
    const data = (await res.json()) as Record<string, unknown>;
    if (!res.ok || !data.bootstrap_assertion) {
      throw new Error("bootstrap_failed");
    }
    return {
      bootstrap_assertion: String(data.bootstrap_assertion),
      expires_at: String(data.expires_at || ""),
      nonce: String(data.nonce || ""),
      session_id: String(data.session_id || session_id),
      tenant_id: data.tenant_id ? String(data.tenant_id) : undefined,
    };
  }

  private async callVerify(boot: BootstrapPayload, scenario: DemoScenario | null): Promise<VerifyResponse> {
    const loc = this.currentLocation();
    let domain = loc.hostname;
    let path = loc.pathname;
    let formId = this.formContextId();
    let sessionId = boot.session_id;
    let verifyUrl = this.config.verifyUrl;

    if (scenario === "WRONG_DOMAIN_SIMULATION") domain = "evil.example";
    if (scenario === "WRONG_PATH_SIMULATION") path = "/demo/evil-path";
    if (scenario === "WRONG_FORM_SIMULATION") formId = "evil_form";
    if (scenario === "CONTROL_TOWER_OFFLINE_SIMULATION") {
      verifyUrl = "http://127.0.0.1:9/offline";
    }

    this.timeline("VERIFY_REQUESTED", { domain, path, form_id: formId });
    const fetchImpl = this.config.fetchImpl || fetch;
    const res = await fetchImpl(verifyUrl, {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({
        bootstrap_assertion: boot.bootstrap_assertion,
        domain,
        path,
        form_id: formId,
        session_id: sessionId,
        nonce: boot.nonce,
      }),
    });
    if (!res.ok) throw new Error("verify_http_error");
    return (await res.json()) as VerifyResponse;
  }

  private applyVerifyResult(result: VerifyResponse): void {
    this.lastVerdict = result.verdict;
    this.lastReason = result.reason_code;
    if (result.verdict === "OK" && result.authenticity_token && result.expires_at) {
      const loc = this.currentLocation();
      // Store verified context as actual browser context (not spoofed scenario domain)
      this.token = result.authenticity_token;
      this.tokenExpiresAtMs = Date.parse(result.expires_at);
      this.verifiedDomain = loc.hostname;
      this.verifiedPath = loc.pathname;
      this.verifiedFormId = this.formContextId();
      this.verifiedSessionId = this.bootstrap?.session_id || null;
      this.setState("VERIFIED", result.reason_code);
      this.timeline("VERDICT_OK", { reason_code: result.reason_code, expires_at: result.expires_at });
      return;
    }
    this.token = null;
    this.tokenExpiresAtMs = 0;
    if (result.verdict === "FAKE") {
      this.setState("FAKE", result.reason_code);
      this.timeline("VERDICT_FAKE", { reason_code: result.reason_code });
    } else {
      this.setState("SUSPICIOUS", result.reason_code);
      this.timeline("VERDICT_SUSPICIOUS", { reason_code: result.reason_code });
    }
  }

  private contextDrifted(): boolean {
    if (!this.verifiedDomain) return false;
    const loc = this.currentLocation();
    if (loc.hostname !== this.verifiedDomain) return true;
    if (loc.pathname !== this.verifiedPath) return true;
    if (this.formContextId() !== this.verifiedFormId) return true;
    if (this.bootstrap && this.verifiedSessionId && this.bootstrap.session_id !== this.verifiedSessionId) {
      return true;
    }
    return false;
  }

  private clearAuth(state: AuthState, reason: string): void {
    this.token = null;
    this.tokenExpiresAtMs = 0;
    this.verifiedDomain = null;
    this.verifiedPath = null;
    this.verifiedFormId = null;
    this.verifiedSessionId = null;
    this.lastReason = reason;
    this.setState(state, reason);
  }

  private setState(state: AuthState, reason?: string): void {
    this.state = state;
    if (reason) this.lastReason = reason;
    this.timeline("STATE_CHANGED", { state, reason_code: this.lastReason });
    this.renderIndicator();
    this.renderTrustSignal();
    const detail = this.getDetail();
    this.config.onStateChange?.(state, detail);
  }

  private renderIndicator(): void {
    if (!this.indicatorEl) return;
    const detail = this.getDetail();
    this.indicatorEl.dataset.state = detail.state;
    this.indicatorEl.className = `cs-indicator cs-${detail.state.toLowerCase()}`;
    this.indicatorEl.setAttribute("role", "status");
    this.indicatorEl.setAttribute("aria-live", "polite");
    this.indicatorEl.innerHTML = `<span class="cs-indicator-icon" aria-hidden="true"></span><span class="cs-indicator-text">${detail.message}</span>`;
  }

  private startRefreshLoop(): void {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    this.refreshTimer = setInterval(() => {
      if (this.destroyed) return;
      if (this.contextDrifted()) {
        this.timeline("CONTEXT_INVALIDATED", { reason: "CONTEXT_DRIFT" });
        this.clearAuth("BLOCKED", "CONTEXT_CHANGED");
        return;
      }
      if (this.token && this.now() >= this.tokenExpiresAtMs) {
        this.timeline("TOKEN_EXPIRED");
        this.clearAuth("EXPIRED", "TOKEN_EXPIRED");
      }
      // React to runtime proof expiry → STALE without leaving a permanent VERIFIED badge.
      if (this.refreshRuntimeProofExpiry()) {
        this.syncRuntimeAuthState();
        this.renderTrustSignal();
      }
    }, 1000);
  }

  private currentLocation(): { hostname: string; pathname: string; origin: string } {
    if (this.config.location) return this.config.location;
    if (typeof window !== "undefined" && window.location) {
      return {
        hostname: window.location.hostname,
        pathname: window.location.pathname,
        origin: window.location.origin,
      };
    }
    return { hostname: "localhost", pathname: "/", origin: "http://localhost" };
  }

  private activeScenario(): DemoScenario | null {
    if (!this.config.allowDemoScenarios) return null;
    return this.config.demoScenario || "VALID";
  }

  private now(): number {
    return (this.config.now || Date.now)();
  }

  private timeline(type: TimelineEventType, detail?: Record<string, unknown>): void {
    const event: TimelineEvent = {
      ts: new Date(this.now()).toISOString(),
      type,
      detail,
    };
    this.config.onTimeline?.(event);
  }

  private mask(value?: string | null): string | null {
    if (!value) return null;
    if (value.length <= 12) return "***";
    return `${value.slice(0, 6)}…${value.slice(-4)}`;
  }
}

interface RuntimeBootstrapInput {
  bootstrapUrl: string;
  flowKey: string;
  formContextId: string;
  configVersion: number;
  hostname?: string;
  path?: string;
}

export async function init(config: ColorShieldConfig): Promise<ColorShieldClient> {
  const client = new ColorShieldClient(config);
  await client.start();
  return client;
}

export { STATE_MESSAGES, generateBrowserNonce };
export {
  deriveVisualTrustState,
  buildVisualTrustDetail,
  VISUAL_TRUST_COPY,
  visualTrustDomContainsSecrets,
} from "./visual-trust";
export type { VisualTrustDetail } from "./visual-trust";
export type {
  AuthState,
  ColorShieldConfig,
  DemoScenario,
  RuntimeTrustState,
  StateDetail,
  SubmitAuthorization,
  SubmitAuthorizationDecision,
  SubmitGateState,
  TimelineEvent,
  TimelineEventType,
  VerifyResponse,
  VisualTrustState,
} from "./types";
